import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import { Box, ButtonBase, TableCell, TableRow, useTheme } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

import CustomCheckbox from '@/components/Checkbox';
import DateText from '@/components/DateText';
import { DateTextVariants } from '@/components/DateText/useDateText';
import IndeterminateCheckbox from '@/components/IndeterminateCheckbox';
import useSelectionFunctions from '@/features/SymbolSelector/hooks/useSelectionFunctions';
import useUserDetails from '@/hooks/useUserDetails';
import RestrictedIconWithTooltip from '@/pages/Markets/MarketsComponents/CommoditiesTable/CommoditiesTableTab/CommoditiesTableRow/RestrictedIconWithTooltip';
import { formatNumberWithPrecision } from '@/utils/formatNumberWithPrecision';
import { getDiffColor } from '@/utils/getDiffColor';

import CollapsedRow from './CollapsedRow';
import useCommoditiesSelection from '../../useCommoditiesSelection';

import type { CommoditiesTableRowType, CommoditiesTableSectionType } from '../../../types';

const CommoditiesTableRow = ({
  row,
  sections,
  index,
  shouldRenderDiff = true,
  shouldRenderDiffChange = true,
  shouldRenderLast = true,
  shouldRenderLastChange = true
}: {
  row: CommoditiesTableRowType;
  sections: CommoditiesTableSectionType[];
  index: number;
  shouldRenderDiff?: boolean;
  shouldRenderDiffChange?: boolean;
  shouldRenderLast?: boolean;
  shouldRenderLastChange?: boolean;
}) => {
  const theme = useTheme();
  const {
    shortTitle,
    date,
    last,
    delta,
    diff,
    diffDelta,
    symbol: { key },
    diffSymbol: { key: diffKey }
  } = row;
  const lastSymbol = key;
  const diffSymbol = diffKey;
  const { isUserExpired } = useUserDetails();
  const restrictedData = (!last && !diff) || isUserExpired;
  const rowRef = useRef<HTMLTableRowElement | null>(null);

  const returnBackgroundColor = () => {
    if (restrictedData) return theme.palette.table.infoRowBg;
    if (index % 2 === 1) return 'secondary.main';
    return 'inherit';
  };
  const [open, setOpen] = useState(false);
  const { isSymbolSelected, toggleSymbolInSelection } = useSelectionFunctions();
  const { toggleCodeInSelection } = useCommoditiesSelection(sections);
  const posthog = usePostHog();

  const trackTableRowClick = (row: CommoditiesTableRowType) => {
    posthog.capture('table_row_click', {
      symbol: row.symbol,
      diffSymbol: row.diffSymbol
    });
  };

  const renderRestrictedOrValue = (value: ReactNode, restricted: boolean | undefined) =>
    restricted ? <RestrictedIconWithTooltip /> : value;

  return (
    <>
      <TableRow
        key={shortTitle}
        ref={rowRef}
        className='markets-table-row'
        sx={{
          '&:last-child td, &:last-child th': { pb: 1 },
          backgroundColor: returnBackgroundColor(),
          '&:first-of-type td, &:first-of-type th': { pt: 1 }
        }}
        onClick={() => {
          trackTableRowClick(row);
        }}
      >
        <TableCell
          sx={theme => ({
            p: 0,
            pl: 1,
            pr: 1,
            borderLeft: open ? `2px solid ${theme.palette.primary.main}` : 'none'
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ButtonBase
              aria-label='expand row'
              disabled={restrictedData}
              style={{ paddingRight: 5 }}
              onClick={() => {
                setOpen(!open);
              }}
            >
              <KeyboardArrowUp
                sx={{
                  fontSize: 14,
                  transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform cubic-bezier(0.5, 0, 0.5, 1) 0.1s'
                }}
              />
            </ButtonBase>
            {lastSymbol.Code && diffSymbol.Code ? (
              <IndeterminateCheckbox
                disabled={restrictedData}
                firstSelected={isSymbolSelected(lastSymbol)}
                secondSelected={isSymbolSelected(diffSymbol)}
                onChange={() => {
                  toggleCodeInSelection(
                    row,
                    isSymbolSelected(lastSymbol) && isSymbolSelected(diffSymbol)
                  );
                }}
              />
            ) : null}
            {lastSymbol.Code && !diffSymbol.Code ? (
              <CustomCheckbox
                checked={isSymbolSelected(lastSymbol)}
                disabled={restrictedData}
                onChange={() => {
                  toggleSymbolInSelection(lastSymbol);
                }}
              />
            ) : null}{' '}
            {!lastSymbol.Code && diffSymbol.Code ? (
              <CustomCheckbox
                checked={isSymbolSelected(diffSymbol)}
                disabled={restrictedData}
                onChange={() => {
                  toggleSymbolInSelection(diffSymbol);
                }}
              />
            ) : null}
          </Box>
        </TableCell>
        <TableCell
          component='th'
          scope='row'
          sx={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {shortTitle}
        </TableCell>
        <TableCell align='right'>
          <DateText
            dateString={date}
            variant={DateTextVariants.DAY_MONTH}
          />
        </TableCell>
        {shouldRenderLast && (
          <TableCell align='right'>
            {renderRestrictedOrValue(last ? formatNumberWithPrecision(last) : '', restrictedData)}
          </TableCell>
        )}
        {shouldRenderLastChange && (
          <TableCell
            align='right'
            sx={{ color: getDiffColor(Number(delta)) }}
          >
            {renderRestrictedOrValue(delta ? formatNumberWithPrecision(delta) : '', isUserExpired)}
          </TableCell>
        )}
        {shouldRenderDiff && (
          <TableCell align='right'>
            {renderRestrictedOrValue(diff ? formatNumberWithPrecision(diff) : '', restrictedData)}
          </TableCell>
        )}
        {shouldRenderDiffChange && (
          <TableCell
            align='right'
            sx={{ color: isUserExpired ? '' : getDiffColor(Number(diffDelta)) }}
          >
            {renderRestrictedOrValue(
              diffDelta ? formatNumberWithPrecision(diffDelta) : '',
              restrictedData
            )}
          </TableCell>
        )}
      </TableRow>
      <TableRow
        sx={{
          backgroundColor: returnBackgroundColor()
        }}
      >
        <CollapsedRow
          diffSymbol={diffSymbol}
          lastSymbol={lastSymbol}
          open={open}
          row={row}
        />
      </TableRow>
    </>
  );
};

export default CommoditiesTableRow;
